import React, { useCallback } from "react";
import { Box, List, ListItem, ListItemButton, ListItemText, Typography } from "@mui/material";
import mapboxgl from "mapbox-gl";

import MapPopup from "fond/map/MapPopup";

import { SourceFeature } from "./types";

interface SourceSelectionPopupProps {
  sources: SourceFeature[];
  lngLat: mapboxgl.LngLatLike;
  onSelect: (source: SourceFeature) => void;
  onClose: () => void;
}

/**
 * Popup component for selecting from multiple overlapping sources.
 * Displays a list of sources with their FondID, color indicator, and remaining capacity.
 */
const SourceSelectionPopup: React.FC<SourceSelectionPopupProps> = ({ sources, lngLat, onSelect, onClose }) => {
  const handleSourceClick = useCallback(
    (source: SourceFeature) => () => {
      onSelect(source);
    },
    [onSelect]
  );

  const getCapacityDisplay = (remainingCapacity: number | null): string => {
    if (remainingCapacity === null) {
      return "Unknown capacity";
    }
    if (remainingCapacity < 0) {
      return `${Math.abs(remainingCapacity)}F over capacity`;
    }
    if (remainingCapacity === 0) {
      return `At capacity`;
    }
    return `${remainingCapacity}F capacity remaining`;
  };

  return (
    <MapPopup lngLat={lngLat} onClose={onClose} title="Select Source">
      <Box sx={{ maxHeight: 300, overflow: "auto", minWidth: 250 }}>
        <List dense disablePadding>
          {sources.map((source) => {
            const { fondId, color, remainingCapacity } = source.properties;
            const capacityDisplay = getCapacityDisplay(remainingCapacity);
            const isOverCapacity = remainingCapacity !== null && remainingCapacity < 0;

            return (
              <ListItem key={fondId} disablePadding>
                <ListItemButton onClick={handleSourceClick(source)}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: color,
                      mr: 1.5,
                      flexShrink: 0,
                      border: "1px solid rgba(0,0,0,0.2)",
                    }}
                  />
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={500}>
                        {fondId}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color={isOverCapacity ? "error" : "text.secondary"}>
                        {capacityDisplay}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>
    </MapPopup>
  );
};

export default SourceSelectionPopup;
